import React, { Component, Fragment } from 'react';
import { Layout, Typography, Button, Row, Col, Card, Form, Input, Spin, message } from 'antd';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import AdminPortalHeader from '../../components/common/admin_portal_header';
import { getVetDetailsForAdmin, createVetForAdmin, updateVetForAdmin } from '../../api/pets-api';

const { Title } = Typography;
const { Content } = Layout;

class AdminVetDetails extends Component {
    formRef = React.createRef();

    constructor(props) {
        super(props);
        this.state = {
            vetId: '',
            vet: null,
            loading: true,
            saving: false,
            isNewVet: false,
        };
    }

    async componentWillMount() {
        var splittedPath = this.props.location.pathname.split('/');
        let id = splittedPath[splittedPath.length - 1];
        
        if (id === 'new') {
            // Creating a new vet
            this.setState({ 
                isNewVet: true, 
                loading: false,
                vet: {
                    clinicName: '',
                    firstName: '',
                    lastName: '',
                    emailAddress: '',
                    phone: '',
                    area: '',
                }
            });
        } else {
            // Editing existing vet
            await this.setState({ vetId: id, isNewVet: false });
            await this.getVet(id);
        }
    }

    async getVet(id) {
        this.setState({ loading: true });
        try {
            let respo = await getVetDetailsForAdmin(id);
            
            if (respo.status === 200 && respo.data.status === 'ok') {
                this.setState({ vet: respo.data.vet, loading: false });
                // Populate form with existing data
                if (this.formRef.current) {
                    this.formRef.current.setFieldsValue(respo.data.vet);
                }
            } else {
                this.setState({ loading: false });
                message.error('Failed to fetch vet details');
            }
        } catch (ex) {
            console.error('Error fetching vet:', ex);
            this.setState({ loading: false });
            if (ex.response && ex.response.status === 404) {
                message.error('Vet not found');
            } else {
                message.error('Error loading vet details');
            }
        }
    }

    handleSave = async (values) => {
        const { isNewVet, vetId } = this.state;
        this.setState({ saving: true });

        try {
            let response;
            if (isNewVet) {
                response = await createVetForAdmin(values);
            } else {
                response = await updateVetForAdmin(vetId, values);
            }

            if (response.status === 200 || response.status === 201) {
                message.success(isNewVet ? 'Vet created successfully' : 'Vet updated successfully');
                // Navigate back to vets list after a short delay
                setTimeout(() => {
                    this.navigateBack();
                }, 1000);
            } else {
                message.error(isNewVet ? 'Failed to create vet' : 'Failed to update vet');
            }
        } catch (error) {
            console.error('Error saving vet:', error);
            if (error.response && error.response.status === 400) {
                message.error('Validation error: Please check all fields');
            } else {
                message.error(isNewVet ? 'Error creating vet' : 'Error updating vet');
            }
        } finally {
            this.setState({ saving: false });
        }
    };

    navigateBack = () => {
        window.location.pathname = '/admin_vets';
    };

    render() {
        const { vet, loading, saving, isNewVet } = this.state;

        if (loading) {
            return (
                <Layout className="dashboard vet-details">
                    <AdminPortalHeader />
                    <div className="m-2 text-center" style={{ paddingTop: '100px' }}>
                        <Spin size="large" />
                    </div>
                </Layout>
            );
        }

        if (!vet && !isNewVet) {
            return (
                <Layout className="dashboard vet-details">
                    <AdminPortalHeader />
                    <div className="m-2">
                        <Button
                            type="primary"
                            icon={<ArrowLeftOutlined />}
                            onClick={this.navigateBack}
                            className="mb-3"
                        >
                            Back to Vets
                        </Button>
                        <Card>
                            <Title level={4}>Vet not found</Title>
                        </Card>
                    </div>
                </Layout>
            );
        }

        return (
            <Layout className="dashboard vet-details">
                <AdminPortalHeader />
                <div className="m-2">
                    <div className="mb-3">
                        <Button
                            type="primary"
                            icon={<ArrowLeftOutlined />}
                            onClick={this.navigateBack}
                        >
                            Back to Vets
                        </Button>
                    </div>

                    <Title level={2} className="text-green font-600 ps-3 m-0">
                        {isNewVet ? 'Create New Vet' : 'Edit Vet Details'}
                    </Title>

                    <Row gutter={15} className="pt-3">
                        <Col xs={24} sm={24} md={16} lg={16} xl={16}>
                            <Card title="Vet Information" className="mb-3">
                                <Form
                                    ref={this.formRef}
                                    layout="vertical"
                                    onFinish={this.handleSave}
                                    initialValues={vet}
                                >
                                    <Row gutter={16}>
                                        <Col span={24}>
                                            <Form.Item
                                                label="Clinic Name"
                                                name="clinicName"
                                                rules={[
                                                    {
                                                        required: true,
                                                        message: 'Please enter clinic name',
                                                    },
                                                ]}
                                            >
                                                <Input placeholder="Enter clinic name" size="large" />
                                            </Form.Item>
                                        </Col>
                                    </Row>

                                    <Row gutter={16}>
                                        <Col span={12}>
                                            <Form.Item
                                                label="First Name"
                                                name="firstName"
                                                rules={[
                                                    {
                                                        required: true,
                                                        message: 'Please enter first name',
                                                    },
                                                ]}
                                            >
                                                <Input placeholder="Enter first name" size="large" />
                                            </Form.Item>
                                        </Col>
                                        <Col span={12}>
                                            <Form.Item
                                                label="Last Name"
                                                name="lastName"
                                                rules={[
                                                    {
                                                        required: true,
                                                        message: 'Please enter last name',
                                                    },
                                                ]}
                                            >
                                                <Input placeholder="Enter last name" size="large" />
                                            </Form.Item>
                                        </Col>
                                    </Row>

                                    <Row gutter={16}>
                                        <Col span={12}>
                                            <Form.Item
                                                label="Email Address"
                                                name="emailAddress"
                                                rules={[
                                                    {
                                                        required: true,
                                                        message: 'Please enter email address',
                                                    },
                                                    {
                                                        type: 'email',
                                                        message: 'Please enter a valid email address',
                                                    },
                                                ]}
                                            >
                                                <Input placeholder="Enter email address" size="large" />
                                            </Form.Item>
                                        </Col>
                                        <Col span={12}>
                                            <Form.Item
                                                label="Phone"
                                                name="phone"
                                                rules={[
                                                    {
                                                        required: true,
                                                        message: 'Please enter phone number',
                                                    },
                                                ]}
                                            >
                                                <Input placeholder="Enter phone number" size="large" />
                                            </Form.Item>
                                        </Col>
                                    </Row>

                                    <Row gutter={16}>
                                        <Col span={24}>
                                            <Form.Item
                                                label="Area"
                                                name="area"
                                                rules={[
                                                    {
                                                        required: true,
                                                        message: 'Please enter area',
                                                    },
                                                ]}
                                            >
                                                <Input placeholder="Enter area" size="large" />
                                            </Form.Item>
                                        </Col>
                                    </Row>

                                    <Form.Item>
                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            icon={<SaveOutlined />}
                                            size="large"
                                            loading={saving}
                                            block
                                        >
                                            {isNewVet ? 'Create Vet' : 'Update Vet'}
                                        </Button>
                                    </Form.Item>
                                </Form>
                            </Card>
                        </Col>
                    </Row>
                </div>
            </Layout>
        );
    }
}

export default AdminVetDetails;
